import { rgba } from 'polished';

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
    margin: [0, 0, 0],
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.24),
    padding: [20, 16, 16],
    backgroundColor: rgba(theme.color.background.dark, 0.28),
    position: 'relative',
    opacity: ({ energy }) => energy && energy.animate ? 0 : 1,
    transition: [
      `border-color ${theme.animation.time}ms ease-out`,
      `background-color ${theme.animation.time}ms ease-out`,
      `box-shadow ${theme.animation.time}ms ease-out`,
      `transform ${theme.animation.time}ms ease-out`
    ].join(','),

    '&:hover': {
      borderColor: rgba(theme.color.secondary.main, 0.62),
      backgroundColor: rgba(theme.color.secondary.main, 0.045),
      boxShadow: `0 0 20px ${rgba(theme.color.secondary.main, 0.12)}`,
      transform: 'translateY(-2px)'
    }
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: '1px solid ' + rgba(theme.color.secondary.dark, 0.18)
  },
  userBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 12,
    textDecoration: 'none',
    color: theme.color.heading.main,
    transition: `color ${theme.animation.time}ms ease-out`,

    '&:hover': {
      color: theme.color.secondary.light
    }
  },
  icon: {
    fontSize: 22,
    color: theme.color.secondary.main,
    filter: `drop-shadow(0 0 5px ${theme.color.secondary.main})`,
    lineHeight: 1,
    marginRight: 10
  },
  userText: {
    fontFamily: theme.typography.primary,
    fontSize: 14,
    color: theme.color.secondary.main,
    letterSpacing: 1,
    textShadow: `0 0 5px ${theme.color.secondary.main}`
  },
  statsBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: [4, 10],
    backgroundColor: rgba(theme.color.secondary.main, 0.08),
    border: '1px solid ' + rgba(theme.color.secondary.main, 0.32),
    fontSize: 11,
    fontFamily: theme.typography.secondary,
    color: theme.color.secondary.light
  },
  statsNumber: {
    color: theme.color.tertiary.main,
    fontWeight: 'bold',
    fontFamily: theme.typography.primary,
    fontSize: 13,
    textShadow: `0 0 5px ${theme.color.tertiary.main}`,
    marginRight: 5
  },
  statsLabel: {
    color: theme.color.text.main,
    fontFamily: theme.typography.secondary,
    fontSize: 11
  },
  scrollContainer: {
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    boxSizing: 'border-box',
    padding: [4, 0, 8],
    WebkitOverflowScrolling: 'touch',

    '&::-webkit-scrollbar': {
      height: 4
    },
    '&::-webkit-scrollbar-track': {
      background: rgba(theme.color.background.dark, 0.6)
    },
    '&::-webkit-scrollbar-thumb': {
      background: rgba(theme.color.secondary.main, 0.35),
      borderRadius: 2,
      '&:hover': {
        background: theme.color.secondary.main
      }
    }
  },
  svg: {
    display: 'block',
    minWidth: 680,
    width: 680,
    maxWidth: 'none',
    height: 'auto',
    fontFamily: theme.typography.secondary,
    userSelect: 'none'
  },
  monthText: {
    fontSize: 10,
    fill: theme.color.primary.dark,
    fontFamily: theme.typography.secondary
  },
  dayText: {
    fontSize: 9,
    fill: rgba(theme.color.primary.dark, 0.65),
    fontFamily: theme.typography.secondary
  },
  cell: {
    rx: 2,
    ry: 2,
    cursor: 'pointer',
    transition: 'stroke 0.15s ease-out, filter 0.15s ease-out, transform 0.15s ease-out',

    '&:hover': {
      stroke: theme.color.secondary.light,
      strokeWidth: 1.5,
      filter: `drop-shadow(0 0 6px ${theme.color.secondary.main})`
    }
  },
  footer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    marginTop: 14,
    paddingTop: 12,
    borderTop: '1px solid ' + rgba(theme.color.secondary.dark, 0.16),
    fontSize: 11,
    color: theme.color.text.main,
    fontFamily: theme.typography.secondary
  },
  legend: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4
  },
  legendLabelLess: {
    marginRight: 6,
    color: theme.color.primary.dark,
    fontSize: 10
  },
  legendLabelMore: {
    marginLeft: 6,
    color: theme.color.primary.dark,
    fontSize: 10
  },
  legendBox: {
    width: 10,
    height: 10,
    borderRadius: 2,
    display: 'inline-block'
  },
  profileLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    color: theme.color.secondary.main,
    textDecoration: 'none',
    fontSize: 11,
    fontFamily: theme.typography.secondary,
    transition: `color ${theme.animation.time}ms ease-out`,

    '&:hover': {
      color: theme.color.secondary.light,
      textShadow: `0 0 6px ${theme.color.secondary.light}`
    }
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 100,
    color: theme.color.secondary.main,
    fontFamily: theme.typography.secondary,
    fontSize: 12,
    letterSpacing: '0.08em'
  },

  '@media screen and (min-width: 680px)': {
    root: {
      padding: [24, 20, 18]
    },
    header: {
      gap: 16,
      marginBottom: 20,
      paddingBottom: 14
    },
    icon: {
      fontSize: 26
    },
    userText: {
      fontSize: 15
    },
    statsBadge: {
      padding: [6, 14],
      fontSize: 12
    },
    statsNumber: {
      fontSize: 14,
      marginRight: 6
    },
    statsLabel: {
      fontSize: 12
    },
    footer: {
      gap: 16,
      marginTop: 18,
      paddingTop: 14,
      fontSize: 12
    },
    legendLabelLess: {
      fontSize: 11,
      marginRight: 8
    },
    legendLabelMore: {
      fontSize: 11,
      marginLeft: 8
    },
    legendBox: {
      width: 11,
      height: 11
    },
    profileLink: {
      fontSize: 12
    }
  }
});

export { styles };
